/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AppState } from 'src/app/store/app.reducer';
import * as DeviceActions from 'src/app/store/device/device.actions';
import moment from 'moment';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StandardDynamicComponent, StandardNewValidator, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectCurrentDeviceFeatures, selectPasswordValid } from 'src/app/store/device/device.selector';
import { DeviceFeature } from 'src/app/model/device-feature';

@Component({
	selector: 'app-password-dialog',
	templateUrl: './password-dialog.component.html',
	providers: [{
		provide: STEPPER_GLOBAL_OPTIONS, useValue: { showError: true }
	}]
})

export class PasswordDialogComponent implements OnInit, OnDestroy {
	passwordForm: FormGroup;
	passwordVerify$: Observable<boolean | undefined>;
	passwordVerifySub: Subscription | undefined;
	passwordErrorSub: Subscription | undefined;
	deviceFeature$: Observable<DeviceFeature[] | undefined>;
	deviceFeatureSub: Subscription | undefined;
	passwordVerify: boolean;

	lengthPassword: number;

	// design
	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private actions$: Actions,
		private ref: DynamicDialogRef,
		public config: DynamicDialogConfig,
	) {
		this.initSelectors();
	}


	initSelectors(): void {
		// Store selectors
		this.passwordVerify$ = this.store.select(selectPasswordValid);
		this.deviceFeature$ = this.store.select(selectCurrentDeviceFeatures);
	}


	ngOnInit(): void {
		this.lengthPassword = 10;
		this.subscribeAll();
		this.buildForm();

		this.onResize();
		StandardNewValidator.setFirstCompile(true);
	}

	subscribeAll() {
		this.passwordVerifySub = this.passwordVerify$.subscribe(passwordVerify => {
			this.passwordVerify = passwordVerify;
			if (this.passwordVerify) {
				this.closeDialog();
			}
		});
		this.passwordErrorSub = this.actions$.pipe(
			ofType(DeviceActions.passwordVerifyInvalid, DeviceActions.passwordVerifyFailed)
		).subscribe(() => {
			if (!this.passwordForm) {
				return;
			}
			StandardNewValidator.setFirstCompile(false);
			this.passwordForm.controls['Password'].setErrors({ incorrect: true });
		});
		this.deviceFeatureSub = this.deviceFeature$.subscribe(deviceFeatures => {
			const fea = deviceFeatures.find(fe => fe.Name === 'password');
			if (fea !== undefined) {
				this.lengthPassword = fea.Size;
				this.buildForm();
			}
		});
	}

	buildForm() {
		this.passwordForm = new FormGroup({
			'Password': new FormControl('', [Validators.required, Validators.maxLength(this.lengthPassword), Validators.pattern('[0-9]+')]),
		});
	}

	ngOnDestroy(): void {
		this.passwordVerifySub?.unsubscribe();
		this.passwordErrorSub?.unsubscribe();
		this.deviceFeatureSub?.unsubscribe();
	}

	submit(): void {
		StandardNewValidator.setFirstCompile(false);
		this.clearIncorrectError(this.f.Password);
		if (!this.getFormValidationErrors()) {
			this.save();
		}
	}

	clearIncorrectError(control: AbstractControl): void {
		if (control?.errors?.['incorrect']) {
			control.setErrors(null);
			control.updateValueAndValidity();
		}
	}

	getFormValidationErrors(): boolean {
		let atLeastOneError = false;
		Object.keys(this.passwordForm.controls).forEach(key => {
			const controlErrors: ValidationErrors = this.passwordForm.get(key).errors;
			if (controlErrors != null) {
				/* Object.keys(controlErrors).forEach(keyError => {
					console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				}); */
				atLeastOneError = true;
			}
		});
		return atLeastOneError;
	}

	save(): void {
		this.store.dispatch(DeviceActions.passwordVerify({ password: this.f.Password.value }));
	}

	getTime(date: string): string {
		if (date === undefined) {
			return '';
		}
		return moment(date).format('HH:mm');
	}

	get f(): any {
		return this.passwordForm.controls;
	}

	closeDialog(): void {
		this.ref.close();
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	col12Responsive(): string {
		return StandardPageLayout.col6Responsive(this.screenWidth);
	}

	dynamicButtonWidth(): string {
		return StandardPageLayout.dynamicButtonWidth(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-30rem');
	}

	justifyContentStyle(): string {
		return StandardNewValidator.justifyMessagesNewValidator(this.screenWidth);
	}

	newShowErrors(type: PasswordDialogComponent["f"], classType: string): string {
		return StandardNewValidator.newShowErrors(type, classType, this.screenWidth);
	}

	showErrorRequiredMessage(type: PasswordDialogComponent["f"]): boolean {
		return StandardNewValidator.showErrorRequiredMessage(type);
	}

	simpleInputTextLayout(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}

	dynamicDatepicker(): string {
		return StandardDynamicComponent.dynamicDatepicker(this.screenWidth);
	}

	dynamicInputTextDesktopTabletWidth(): string {
		return StandardPageLayout.dynamicInputTextDesktopTabletWidth(this.screenWidth, 'w-20rem', 'w-30rem');
	}

	customPaddingX(): string {
		return StandardPageLayout.customPaddingX(this.screenWidth);
	}

}
